import { Component, Input, OnChanges } from '@angular/core';
import { ContentService } from '@blk/cwp-ng-framework';
import { ComponentData, TabData } from '@ddt-web/data-service';
import { PortfolioSubstitutionAlerts } from '@ddt-web/domain';
import { PartnerConstants } from '@ddt-web/user-data';
import * as find from 'lodash/find';
import * as get from 'lodash/get';

import { AlertConstants } from '../alert/alert.constants';
import { PartnerHoldingsAlertsService } from '../services';
import { AlertUtil } from '../utils/alert-util';
import { EntityFlags } from '../utils/entity-flags';
import { PartnerPortfolioFlagUtils } from '../utils/partner-portfolio-flag-utils';

@Component({
	selector: 'pi-partner-portfolio-alerts',
	templateUrl: './partner-portfolio-alerts.component.html',
	styleUrls: ['./partner-portfolio-alerts.component.scss']
})
export class PartnerPortfolioAlertsComponent implements OnChanges {

	@Input() public data: ComponentData;
	@Input() public portfolioIndex: number;
	@Input() public showListOfInvalidSecurities: boolean = false;
	@Input() public showAggregatedPortfoliosAlerts: boolean = false;

	public AlertConstants = AlertConstants;
	public portfolioFlags: Array<EntityFlags> = new Array<EntityFlags>();
	public portfolioSecuritiesTabData: TabData;

	public hasReallocatedSecurities: boolean;
	public hasCashSubstitutions: boolean;
	public hasAssetClassSubstitutions: boolean;
	public hasBondSubstitutions: boolean;
	public isModelDirty: boolean;
	public hasExtPartnerPortfolioAlerts: boolean;

	public hasPartnerAlerts: boolean = false;

	public alertReallocatedSecuritiesInfoBubble: string = this.contentService.get('ALERT_REALLOCATED_SECURITIES_INFO', AlertConstants.ALERT_REALLOCATED_SECURITIES_INFO);
	public alertCashSubstitutionsInfoBubble: string = this.contentService.get('ALERT_CASH_SUBSTITUTIONS_INFO', AlertConstants.ALERT_CASH_SUBSTITUTIONS_INFO);
	public extPartnerPortfolioAlteredInfoBubble: string = this.contentService.get('EXT_PARTNER_PORTFOLIO_ALTERED_INFO', '');
	public alertExtSubstitutionsInfoBubble: string = this.contentService.get('EXT_PARTNER_PORTFOLIO_SUBSTITUTION_INFO', '');
	public portfolioAlteredInfoBubble: string = this.contentService.get('PORTFOLIO_ALTERED_ALERT_INFO', AlertConstants.PORTFOLIO_ALTERED_ALERT_INFO);

	constructor(private contentService: ContentService, private partnerHoldingsAlertsService: PartnerHoldingsAlertsService) { }

	public ngOnChanges(): void {
		if (this.data.success && this.data.portfolioData) {
			this.portfolioFlags = PartnerPortfolioFlagUtils.populatePartnerPortfolioFlags(this.data);

			if (this.showListOfInvalidSecurities) {
				this.addInvalidSecuritiesToInfoBubble();
			}

			this.setAlertFlags();
		}
	}

	private setAlertFlags(): void {
		const tabData = this.data.getTabById('portfolio.securities');
		const portfolioSubstitutionAlerts: Array<PortfolioSubstitutionAlerts> = this.partnerHoldingsAlertsService.getPortfolioSubstitutionAlerts(tabData);
		let portfolioHasErrors = PartnerPortfolioFlagUtils.hasErrors(this.portfolioFlags[this.portfolioIndex]);

		const portfolioId = this.data.portfolioData[0].find(data => data.config.id === 'id').rawValue as string;

		if (this.showAggregatedPortfoliosAlerts) {
			portfolioHasErrors = this.portfolioFlags.some(portfolioFlag => PartnerPortfolioFlagUtils.hasErrors(portfolioFlag));

			this.hasReallocatedSecurities = this.portfolioFlags.some(portfolioFlag => PartnerPortfolioFlagUtils.hasReallocatedSecurities(portfolioFlag));
			this.hasBondSubstitutions = this.portfolioFlags.some(portfolioFlag => PartnerPortfolioFlagUtils.hasBondSubstitutions(portfolioFlag));
			this.hasCashSubstitutions = portfolioSubstitutionAlerts.some(portfolioAlert => AlertUtil.hasInvalidSecuritySubstitutionAlerts(portfolioAlert)) || portfolioHasErrors;
			this.hasAssetClassSubstitutions = portfolioSubstitutionAlerts.some(portfolioAlert => AlertUtil.hasPortfolioAssetSubstitutionAlerts(portfolioAlert));
			this.isModelDirty = this.portfolioFlags.some(portfolioFlag => PartnerPortfolioFlagUtils.isModelDirty(portfolioFlag)) || portfolioHasErrors;

		} else {
			this.hasReallocatedSecurities = PartnerPortfolioFlagUtils.hasReallocatedSecurities(this.portfolioFlags[this.portfolioIndex]);
			this.hasBondSubstitutions = PartnerPortfolioFlagUtils.hasBondSubstitutions(this.portfolioFlags[this.portfolioIndex]);
			this.hasCashSubstitutions = AlertUtil.hasInvalidSecuritySubstitutionAlerts(portfolioSubstitutionAlerts[this.portfolioIndex]) || portfolioHasErrors;
			this.hasAssetClassSubstitutions = AlertUtil.hasPortfolioAssetSubstitutionAlerts(portfolioSubstitutionAlerts[this.portfolioIndex]);
			this.isModelDirty = PartnerPortfolioFlagUtils.isModelDirty(this.portfolioFlags[this.portfolioIndex]) || portfolioHasErrors;
		}

		this.hasPartnerAlerts = this.isModelDirty || this.hasReallocatedSecurities || this.hasCashSubstitutions || this.hasAssetClassSubstitutions || this.hasBondSubstitutions;
		this.hasExtPartnerPortfolioAlerts = portfolioId?.includes(PartnerConstants.PARTNER.EXT_IDENTIFIER) &&
			!!portfolioSubstitutionAlerts[this.portfolioIndex].invalidSecuritiesAlertsMsgMap.size;
	}

	private addInvalidSecuritiesToInfoBubble(): void {
		const invalidSecuritiesList: string = this.getInvalidSecuritiesList(this.data.getTabById('portfolio.securities'));
		this.alertReallocatedSecuritiesInfoBubble = `${this.alertReallocatedSecuritiesInfoBubble} <br> ${invalidSecuritiesList}`;
		this.alertCashSubstitutionsInfoBubble = `${this.alertCashSubstitutionsInfoBubble} <br> ${invalidSecuritiesList}`;
	}

	private getInvalidSecuritiesList(tabData: TabData): string {
		let listOfInvalidSecurities: string = '';
		const invalidTickers: Set<string> = new Set();

		const tableDataArray = this.showAggregatedPortfoliosAlerts ? tabData.tableData : [tabData.tableData[0]];
		tableDataArray?.filter(tableData => !!tableData)?.forEach((tableDataObj, index) => {
			const rows = get(tableDataObj, 'rows', []);

			if (rows.length) {
				listOfInvalidSecurities +=
					rows.filter(security => security?.positionType?.rawValue === 'INVALID')
						.reduce((accumulator, security) => {
							const ticker = find(get(security, 'otherIdentifiers.rawValue'), { customIdentifierType: 'ORIGIN_TICKER' })
								? find(get(security, 'otherIdentifiers.rawValue'), { customIdentifierType: 'ORIGIN_TICKER' }).identifier
								: find(get(security, 'otherIdentifiers.rawValue'), { identifierType: 'TICKER' }).identifier;

							if (ticker) {
								const invalidSecuritiesMessage = this.getUpdatedInvalidSecurities(security, ticker, accumulator, invalidTickers);
								invalidTickers.add(ticker);

								return invalidSecuritiesMessage;

							} else {
								return '';
							}
						}, '');

				if (index === tableDataArray.length - 1) {
					listOfInvalidSecurities = listOfInvalidSecurities.substring(0, listOfInvalidSecurities.length - 1);
				}
			}
		});

		return listOfInvalidSecurities;
	}

	private getUpdatedInvalidSecurities(security: any, ticker: string, invalidSecurityMessage: string, existentInvalidTickers: Set<string>): string {
		let alert;
		if (this.showAggregatedPortfoliosAlerts) {
			alert = !existentInvalidTickers.has(ticker) ? `${invalidSecurityMessage} ${ticker},` : invalidSecurityMessage;
		} else {
			const weight = this.partnerHoldingsAlertsService.formatAlertPercentage(get(security, 'weight.rawValue', ''));
			alert = `${invalidSecurityMessage} ${ticker} ${weight}%,`;
		}

		return alert;
	}
}
